import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Layouts } from 'react-grid-layout';
import { GRID_COLUMNS } from '../config/appConfig';
import type { WindowKind } from '../data/protocol/types';
import {
  buildDashboardLayouts,
  createWindow,
  floatingLayoutItem,
  titleFor
} from './windowRegistry';
import {
  clearWorkspace,
  loadWorkspace,
  saveWorkspace
} from './workspacePersistence';
import type {
  GraphWindowConfig,
  NewsDetailWindowConfig,
  WindowConfig,
  WindowInstance,
  WorkspaceState
} from './types';

function createInitialWorkspace(): WorkspaceState {
  const windows = [
    createWindow('quotes', 1),
    createWindow(
      'graph',
      2,
      { symbol: 'AAPL' } as Partial<GraphWindowConfig>
    ),
    createWindow('heatmap', 3),
    createWindow('quote', 4, { symbol: 'AAPL' }),
    createWindow('news', 5),
    createWindow('quote', 6, { symbol: 'BAC' })
  ];

  return {
    windows,
    layouts: buildDashboardLayouts(windows),
    activeWindowId: windows[windows.length - 1]?.id
  };
}

export function useWorkspace() {
  const [state, setState] = useState<WorkspaceState>(() => {
    return sanitizeWorkspace(
      loadWorkspace() ?? createInitialWorkspace()
    );
  });

  useEffect(() => {
    saveWorkspace(state);
  }, [state]);

  const setLayouts = useCallback((layouts: Layouts) => {
    setState((current) => {
      return areLayoutsEqual(current.layouts, layouts)
        ? current
        : {
            ...current,
            layouts
          };
    });
  }, []);

  const focusWindow = useCallback((id: string) => {
    setState((current) => {
      const target = current.windows.find((window) => window.id === id);

      if (!target) return current;

      const top = Math.max(
        0,
        ...current.windows.map((window) => window.zIndex)
      );

      if (
        target.zIndex === top &&
        current.activeWindowId === id
      ) {
        return current;
      }

      return {
        ...current,
        activeWindowId: id,
        windows: current.windows.map((window) => {
          return window.id === id
            ? {
                ...window,
                zIndex: top + 1
              }
            : window;
        })
      };
    });
  }, []);

  const addWindow = useCallback((
    kind: WindowKind,
    configOverride?: Partial<WindowConfig>,
    titleOverride?: string
  ) => {
    setState((current) => {
      const zIndex =
        Math.max(
          0,
          ...current.windows.map((window) => window.zIndex)
        ) + 1;

      const window = createWindow(
        kind,
        zIndex,
        configOverride,
        titleOverride
      );

      const nextLayouts = {
        ...current.layouts
      };

      for (
        const breakpoint of Object.keys(
          GRID_COLUMNS
        ) as Array<keyof typeof GRID_COLUMNS>
      ) {
        nextLayouts[breakpoint] = [
          floatingLayoutItem(window, breakpoint),
          ...(nextLayouts[breakpoint] ?? [])
        ];
      }

      return {
        windows: [...current.windows, window],
        layouts: nextLayouts,
        activeWindowId: window.id
      };
    });
  }, []);

  const openGraphForSymbol = useCallback((symbol: string) => {
    const config: Partial<GraphWindowConfig> = {
      symbol
    };

    addWindow('graph', config);
  }, [addWindow]);

  const openNewsDetail = useCallback((newsId: string) => {
    const config: Partial<NewsDetailWindowConfig> = {
      newsId
    };

    addWindow(
      'newsDetail',
      config,
      'News Detail'
    );
  }, [addWindow]);

  const removeWindow = useCallback((id: string) => {
    setState((current) => ({
      windows: current.windows.filter(
        (window) => window.id !== id
      ),
      activeWindowId:
        current.activeWindowId === id
          ? undefined
          : current.activeWindowId,
      layouts: Object.fromEntries(
        Object.entries(current.layouts).map(
          ([breakpoint, layout]) => [
            breakpoint,
            layout.filter((item) => item.i !== id)
          ]
        )
      ) as Layouts
    }));
  }, []);

  const updateWindowConfig = useCallback((
    id: string,
    updater: (config: WindowConfig) => WindowConfig
  ) => {
    setState((current) => ({
      ...current,
      windows: current.windows.map((window) => {
        if (window.id !== id) return window;

        const config = updater(window.config);

        return {
          ...window,
          config,
          title: titleFor(window.kind, config)
        } as WindowInstance;
      })
    }));
  }, []);

  const resetWorkspace = useCallback(() => {
    clearWorkspace();

    setState(
      sanitizeWorkspace(createInitialWorkspace())
    );
  }, []);

  return useMemo(() => ({
    state,
    setLayouts,
    addWindow,
    openGraphForSymbol,
    openNewsDetail,
    removeWindow,
    updateWindowConfig,
    resetWorkspace,
    focusWindow
  }), [
    state,
    setLayouts,
    addWindow,
    openGraphForSymbol,
    openNewsDetail,
    removeWindow,
    updateWindowConfig,
    resetWorkspace,
    focusWindow
  ]);
}

function sanitizeWorkspace(
  state: WorkspaceState
): WorkspaceState {
  const windows = state.windows.map(
    (window, index) => ({
      ...window,
      zIndex: Number(window.zIndex || index + 1),
      title: titleFor(window.kind, window.config)
    })
  );

  const validIds = new Set(
    windows.map((window) => window.id)
  );

  const layouts = Object.fromEntries(
    Object.entries(state.layouts).map(
      ([breakpoint, layout]) => [
        breakpoint,
        layout.filter((item) => validIds.has(item.i))
      ]
    )
  ) as Layouts;

  return {
    windows,
    layouts,
    activeWindowId: state.activeWindowId
  };
}

function areLayoutsEqual(
  left: Layouts,
  right: Layouts
): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (let index = 0; index < leftKeys.length; index += 1) {
    if (leftKeys[index] !== rightKeys[index]) {
      return false;
    }

    const leftLayout = left[leftKeys[index]] ?? [];
    const rightLayout = right[rightKeys[index]] ?? [];

    if (leftLayout.length !== rightLayout.length) {
      return false;
    }

    for (
      let layoutIndex = 0;
      layoutIndex < leftLayout.length;
      layoutIndex += 1
    ) {
      const leftItem = leftLayout[layoutIndex];
      const rightItem = rightLayout[layoutIndex];

      if (
        leftItem.i !== rightItem.i ||
        leftItem.x !== rightItem.x ||
        leftItem.y !== rightItem.y ||
        leftItem.w !== rightItem.w ||
        leftItem.h !== rightItem.h ||
        leftItem.minW !== rightItem.minW ||
        leftItem.minH !== rightItem.minH ||
        leftItem.maxW !== rightItem.maxW ||
        leftItem.maxH !== rightItem.maxH ||
        leftItem.static !== rightItem.static
      ) {
        return false;
      }
    }
  }

  return true;
}